"use client";

import { useEffect, useCallback } from "react";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";

export const STORAGE_KEY = "edx_product_view_history";
const MAX_PRODUCTS = 50;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface ProductViewMetadata {
  price?: number;
  discountedPrice?: number;
  currency?: string;
  partner?: string;
  category?: string;
  enrollmentStart?: string;
  enrollmentEnd?: string;
  courseRunEnd?: string;
}

export interface ProductView {
  productId: string;
  productType: "course" | "program" | "exec-ed" | "degree";
  productName: string;
  productUrl: string;
  checkoutUrl: string;
  courseId?: string;
  viewedAt: number;
  metadata?: ProductViewMetadata;
}

/**
 * Hook to track and store product views in localStorage
 * Tracks views on product detail pages (courses and programs) to enable abandoned cart intent matching.
 *
 * @param productId - Unique identifier for the product
 * @param productType - Type of product: 'course' or 'program'
 * @param productName - Name of the product
 * @param productUrl - Detail page URL
 * @param checkoutUrl - Checkout/enrollment URL
 * @param metadata - Optional metadata (price, discountedPrice, currency, partner, category)
 *                   Note: To avoid unnecessary re-renders, consider memoizing this object with useMemo
 * @returns {object} An object containing:
 *   - getProductViews: Function to retrieve the list of viewed products.
 *   - saveProductView: Function to save a product view to storage.
 *   - clearProductViews: Function to clear all stored product views.
 *   - isStorageAvailable: Function to check if localStorage is currently available (call it to get fresh state).
 */
export function useProductViewStorage(
  productId?: string,
  productType?: "course" | "program" | "exec-ed" | "degree",
  productName?: string,
  productUrl?: string,
  checkoutUrl?: string,
  courseId?: string,
  metadata?: ProductViewMetadata,
) {
  const { flagValue } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_PRODUCT_VIEW_TRACKING",
    "legacy",
  );

  // Check secondary feature flag for search revamp
  const { flagValue: searchRevampFlag } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_SEARCH_BAR_REVAMP_TEST",
    "legacy",
  );

  const isFeatureEnabled = flagValue === "test" || searchRevampFlag === "test";

  /**
   * Check if localStorage is available
   */
  const isLocalStorageAvailable = useCallback((): boolean => {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return false;
    }

    try {
      const testKey = "__localStorage_test__";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  /**
   * Get stored product views
   */
  const getProductViews = useCallback((): ProductView[] => {
    if (!isLocalStorageAvailable()) {
      return [];
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return [];
      }

      const views: ProductView[] = JSON.parse(stored);
      const now = Date.now();

      // Filter out expired views
      const validViews = views.filter(
        (view) => now - view.viewedAt < MAX_AGE_MS,
      );

      // Update storage if we filtered anything out
      if (validViews.length !== views.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(validViews));
      }

      return validViews;
    } catch (e) {
      // If parsing fails, reset storage
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (removeError) {
        // Fail silently if we can't even remove
      }
      return [];
    }
  }, [isLocalStorageAvailable]);

  /**
   * Save a product view to storage
   */
  const saveProductView = useCallback(
    (view: Omit<ProductView, "viewedAt">): void => {
      if (!isLocalStorageAvailable()) {
        return;
      }

      try {
        const existingViews = getProductViews();

        // Remove existing view of the same product
        const filteredViews = existingViews.filter(
          (v) => v.productId !== view.productId,
        );

        // Add new view at the beginning
        const newView: ProductView = {
          ...view,
          viewedAt: Date.now(),
        };

        const updatedViews = [newView, ...filteredViews];

        // Keep only the most recent MAX_PRODUCTS views
        const trimmedViews = updatedViews.slice(0, MAX_PRODUCTS);

        localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedViews));
      } catch (e) {
        // Fail silently if storage is full or unavailable
      }
    },
    [isLocalStorageAvailable, getProductViews],
  );

  /**
   * Clear all product views from storage
   */
  const clearProductViews = useCallback((): void => {
    if (!isLocalStorageAvailable()) {
      return;
    }

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // Fail silently
    }
  }, [isLocalStorageAvailable]);

  /**
   * Remove a single product view by productId
   */
  const removeProductView = useCallback(
    (productIdToRemove: string): void => {
      if (!isLocalStorageAvailable()) {
        return;
      }

      try {
        const existingViews = getProductViews();
        const filteredViews = existingViews.filter(
          (view) =>
            view.productId !== productIdToRemove &&
            view.courseId !== productIdToRemove,
        );
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredViews));
      } catch (e) {
        // Fail silently
      }
    },
    [isLocalStorageAvailable, getProductViews],
  );

  /**
   * Track the product view on mount
   * Using JSON.stringify for metadata to avoid unnecessary re-renders from object reference changes
   */
  useEffect(() => {
    if (
      !isFeatureEnabled ||
      !productId ||
      !productType ||
      !productName ||
      !productUrl ||
      !checkoutUrl
    ) {
      return;
    }

    saveProductView({
      productId,
      productType,
      productName,
      productUrl,
      checkoutUrl,
      courseId,
      metadata,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFeatureEnabled,
    productId,
    productType,
    productName,
    productUrl,
    checkoutUrl,
    courseId,
    // Use JSON.stringify to compare metadata by value instead of reference
    // This prevents unnecessary re-tracking when metadata object reference changes
    // but the content remains the same
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(metadata),
    saveProductView,
  ]);

  return {
    getProductViews,
    saveProductView,
    clearProductViews,
    removeProductView,
    // Return the function itself so it's called on each access, ensuring fresh state
    isStorageAvailable: isLocalStorageAvailable,
  };
}
